import {ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation} from '@angular/core';
import {Store} from '@ngrx/store';
import {CheckAuth, StoreState, TokenStorageService, ChangeLang} from './core';
import {TranslateService} from '@ngx-translate/core';
import {LangStorageService, AppConfigService} from 'core/services';
import {Subject} from "rxjs";
import {initI18nUtil} from 'core/utils/i18n.util';

@Component({
  selector: 'dc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LangStorageService]
})
export class AppComponent implements OnInit {
  mouseEntered = new Subject<void>();

  // Browser language code to app language code mapping
  // Maps ISO 639-1 codes to app-specific codes
  private readonly languageCodeMap: Record<string, string> = {
    'kk': 'kz',  // Map Kazakh ISO code (kk) to app code (kz)
    'uk': 'ua',  // Map Ukrainian ISO code (uk) to app code (ua)
    'en': 'gb'   // Map English ISO code (en) to app code (gb)
  };

  constructor(
    public tokenStorageService: TokenStorageService,
    public store: Store<StoreState>,
    public translate: TranslateService,
    public langStorageService: LangStorageService,
    private appConfigService: AppConfigService
  ) {
    // Initialize i18n utility for global access
    initI18nUtil(this.translate);
  }

  ngOnInit(): void {
    // Load configuration first
    this.appConfigService.loadConfig().subscribe(() => {
      console.log('Configuration loaded');

      // Initialize authentication after config is loaded
      const token = this.tokenStorageService.getValue();
      if (token) {
        this.store.dispatch(new CheckAuth());
      }

      // Detect and set preferred language after config is loaded
      this.initializeLanguage();
    });
  }

  private initializeLanguage(): void {
    // Get configuration from AppConfigService
    const config = this.appConfigService.getConfig();
    const availableLanguages = config.availableLanguages;
    const defaultLanguage = config.defaultLanguage;

    // Determine the best language to use
    let selectedLang: string;

    // Check if user has a stored language preference
    const storedLang = this.langStorageService.getValue();

    if (storedLang && availableLanguages.includes(storedLang)) {
      // Use stored language preference
      selectedLang = storedLang;
    } else {
      // Detect browser language
      let browserLang = this.translate.getBrowserLang();

      // Map browser language code to app language code if needed
      if (browserLang && this.languageCodeMap[browserLang]) {
        browserLang = this.languageCodeMap[browserLang];
      }

      // Check if browser language is supported
      if (browserLang && availableLanguages.includes(browserLang)) {
        selectedLang = browserLang;
      } else {
        // Fallback to default language from config
        selectedLang = defaultLanguage;
      }
    }

    // Set both default and active language to the selected language
    this.translate.setDefaultLang(selectedLang);
    this.translate.use(selectedLang);
    this.store.dispatch(new ChangeLang(selectedLang));
  }
}
